import { useEffect, useRef, useState } from "react";
import { useDevicePixelRatio } from "../../lib/useDevicePixelRatio";
import {
  loadQuoteEngines,
  type MountedQuote,
  mountQuoteSurface,
  type ObserveVisible,
  observeVisibleDefault,
  type QuoteEngines,
} from "./quoteSurface";
import "./quoteCanvas.css";

/**
 * `<QuoteCanvas>` — Row-3's anchored quote, painted by the document engine.
 *
 * See `quoteSurface.ts` for WHY (the quote and the passage were two different
 * renderings of the same words) and for the IR, the page template and the
 * batching choice. This file is the React glue only: when to mount, when to
 * re-lay-out, and what to show when the engine is not there.
 *
 * ── THE TWO STATES, AND WHY THE TEXT ONE IS NOT A PLACEHOLDER ───────────────
 *
 * `data-paint-state="text"` renders the quote as a real `<p>`. It is the state
 * before the card is scrolled to, before ~1.7 MB of wasm has resolved, and
 * FOREVER if the engine cannot load at all (blocked wasm, an offline asset, a
 * transport 5xx). It is deliberately the OLD presentation rather than a spinner
 * or a skeleton: a reviewer who cannot get the engine must still be able to read
 * which passage the note is about. `data-paint-error` names the reason for
 * anyone looking, without putting a wasm error message on a review card.
 *
 * `data-paint-state="painted"` unmounts that `<p>` and the `DocumentMirror`
 * inside the surface becomes the DOM's copy of the words. Exactly ONE projection
 * of the quote is in the accessibility tree in either state — never two reading
 * the same sentence twice.
 *
 * ── WHY THE CANVAS IS NOT INSIDE THE JUMP BUTTON ────────────────────────────
 *
 * Row-3 used to be one `<button>` wrapping the quote text. The surface cannot go
 * inside it, for three separate reasons, and the third alone is decisive:
 *  1. a `<button>`'s content model is phrasing content — the surface's `<div>`
 *     and the mirror's `<article role="document">` are not, and a `role="document"`
 *     inside a button is not something an accessibility tree can express;
 *  2. a button computes its accessible name from its CONTENTS, and every
 *     `<canvas>` is `aria-hidden`, so the button would have become nameless;
 *  3. a drag-select inside a `<button>` still fires `click` on release. Selecting
 *     the quote in order to COPY it would have jumped the document away
 *     mid-gesture, every time.
 *
 * So the quote box is a region and the jump is its own named control (see
 * `CommentThread`). The painted words stay clickable — `onActivate` fires on a
 * click that did not end a drag — and the keyboard path is the button.
 */

export interface QuoteCanvasProps {
  /** The anchor's quote, verbatim off the wire. */
  readonly quote: string;
  /** The anchor is NOT in this document (orphan / never anchored) — painted with
   *  a real docz `strikeout` mark rather than a CSS line. */
  readonly struck?: boolean;
  /** Accessible name for the mirror's landmark. */
  readonly label?: string;
  /** BCP-47 language of the quoted text (WCAG 3.1.2). */
  readonly lang?: string;
  readonly testId?: string;
  /** A pointer click on the painted words that did NOT end a drag-select. */
  readonly onActivate?: () => void;
  /** DI seam (tests / E2E): resolve the two wasm engines. */
  readonly loadEngines?: () => Promise<QuoteEngines>;
  /** DI seam (tests / E2E): decide when this box is worth painting. */
  readonly observeVisible?: ObserveVisible;
}

type PaintState =
  | { readonly kind: "text"; readonly error?: string }
  | { readonly kind: "painted"; readonly widthCss: number; readonly heightCss: number };

export function QuoteCanvas({
  quote,
  struck = false,
  label = "Quoted from the document",
  lang,
  testId = "quote-canvas",
  onActivate,
  loadEngines = loadQuoteEngines,
  observeVisible,
}: QuoteCanvasProps) {
  const dpr = useDevicePixelRatio(2);
  const rootRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef<MountedQuote | null>(null);
  const [visible, setVisible] = useState(false);
  const [paint, setPaint] = useState<PaintState>({ kind: "text" });

  // Keep `onActivate` current without re-running the mount effect: a parent
  // re-render must not tear the surface down and re-run the wasm layout.
  const activateRef = useRef(onActivate);
  activateRef.current = onActivate;

  // ── when is this box worth painting? ───────────────────────────────────────
  useEffect(() => {
    const root = rootRef.current;
    if (!root || visible) return;
    const observe = observeVisible ?? observeVisibleDefault();
    let live = true;
    const stop = observe(root, () => {
      if (live) setVisible(true);
    });
    return () => {
      live = false;
      stop();
    };
  }, [observeVisible, visible]);

  // ── mount the surface ──────────────────────────────────────────────────────
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !visible || quote.trim() === "") return;
    let disposed = false;

    void (async () => {
      try {
        const engines = await loadEngines();
        if (disposed) return;
        const mounted = mountQuoteSurface(engines, {
          mount,
          quote,
          struck,
          label,
          ...(lang !== undefined ? { lang } : {}),
          testId: `${testId}-mirror`,
          dpr,
          onActivate: () => activateRef.current?.(),
          // Measured off the ROOT, never off the mount. The mount is the element
          // the surface owns and this component keeps it out of the flow until the
          // first paint, so its `clientWidth` is 0 at exactly the moment the page
          // template needs a column width — and the quote then painted a 320px
          // sheet inside a 539px card row (measured in a real browser). The root is
          // always laid out.
          widthCss: () => rootRef.current?.clientWidth ?? 0,
        });
        if (mounted == null) return;
        if (disposed) {
          mounted.destroy();
          return;
        }
        mountedRef.current = mounted;
        setPaint({
          kind: "painted",
          widthCss: mounted.paintedWidthCss,
          heightCss: mounted.paintedHeightCss,
        });
        // Web fonts land after the first paint; the engine's own metrics do not
        // change (pdfz measured them), but the raster does.
        const fonts = (globalThis as { document?: { fonts?: FontFaceSet } }).document
          ?.fonts;
        if (fonts?.ready) {
          void fonts.ready.then(() => {
            if (!disposed) mountedRef.current?.surface.repaint();
          });
        }
      } catch (err) {
        if (disposed) return;
        // HONEST, and READABLE. The quote falls back to text so the reviewer can
        // still see which passage the note is about — the one thing this row
        // exists to tell them.
        setPaint({
          kind: "text",
          error: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => {
      disposed = true;
      mountedRef.current?.destroy();
      mountedRef.current = null;
    };
  }, [visible, quote, struck, label, lang, testId, dpr, loadEngines]);

  // ── a rail resize re-lays-out at the new row width ─────────────────────────
  // The row width is the quote box's COLUMN width, so a narrower rail must re-wrap
  // the quote — not rescale it (`canvasPresentation`: never auto-shrink). One
  // observer per painted box, and it does nothing until the width actually moves.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || paint.kind !== "painted") return;
    const Ctor = (globalThis as { ResizeObserver?: typeof ResizeObserver })
      .ResizeObserver;
    if (typeof Ctor !== "function") return;
    // The ROOT, for the same reason the width is measured off it: the mount's box
    // is the engine's own output, so observing it would watch the effect rather
    // than the cause and could feed a resize back into itself.
    let lastWidth = root.clientWidth;
    const ro = new Ctor(() => {
      const next = root.clientWidth;
      if (next === lastWidth) return;
      lastWidth = next;
      mountedRef.current?.refresh();
    });
    ro.observe(root);
    return () => ro.disconnect();
  }, [paint.kind]);

  return (
    <span
      className="ela-quote"
      ref={rootRef}
      data-testid={testId}
      data-paint-state={paint.kind}
      {...(paint.kind === "text" && paint.error !== undefined
        ? { "data-paint-error": paint.error }
        : {})}
      {...(struck ? { "data-struck": "" } : {})}
    >
      {/* The surface's mount. It carries NO React children — `createCanvasSurface`
          calls `container.replaceChildren()` and then owns this subtree, and the
          `DocumentMirror` is appended to it imperatively. Mixing React children in
          here would have React and the engine fight over the same node. */}
      <span className="ela-quote__surface" ref={mountRef} />
      {paint.kind === "text" ? (
        <span className="ela-quote__text" data-testid={`${testId}-text`}>
          {struck ? <s>{quote}</s> : quote}
        </span>
      ) : null}
    </span>
  );
}
